import { onBeforeMount } from 'vue'
import { v4 as uuidv4 } from 'uuid'

import { useDataLayer } from '@toni/brand-ui'

import { usePageStore } from '../../stores/page'
import { registerSlots } from '../../utils/use-ad-provider'
import { metaDataSchema } from '../../utils/meta-data-schema'
import { pageMetaSchema } from '../../utils/page-meta-schema'

import type { PageBody } from '../../utils/page-body-schema'

// See: https://bauer-specialist.atlassian.net/wiki/spaces/PPOC/pages/4456448008/GA4+Data+Layer#Definition
type ArticleDataUsedForTracking = {
  aiGeneratedContent: boolean,
  affiliateContent: boolean,
  articleId: number | string,
  articleType: 'article' | 'recipe' | 'gallery' | 'video' | 'NotSet',
  authorName: string,
  brand: string,
  categories: string,
  charCount: number,
  containsAffiliate: boolean,
  country: 'DE' | 'GB',
  dataLayerVersion: number,
  datePublished: string,
  dateChanged: string,
  dateCreated: string,
  displayDate: string,
  embedTypes: string,
  embedTypesCount: number,
  evergreenContent: boolean,
  hasAds: boolean,
  hasGallery: boolean,
  hasProductCards: boolean,
  hasVideo: boolean,
  isContentCommerce: boolean,
  mainCategory: string,
  mainTag: string,
  shorttermContent: boolean,
  pageId: string,
  pageviewId: string,
  pageType: 'article' | 'seriesArticle' | 'structureterm' | 'NotSet',
  paragraphCount: number,
  paragraphTypes: string,
  potentialAdCount: number,
  productCardIds: string,
  productCardsCount: number,
  roofline: string,
  subCategory: string,
  tags: string,
  teaserContent: string,
  videoIds: string,
  wordCount: number
}

export function useArticleTracking (brand: string) {
  const dataLayer = useDataLayer()
  const { pageData } = usePageStore()

  onBeforeMount(async () => {
    if (!pageData?.body || !pageData?.pageMeta || !pageData?.metaData) return

    const parsedBody = pageData.body as PageBody
    const parsedPageMetaData = pageMetaSchema.safeParse(pageData.pageMeta)
    const parsedMetaData = metaDataSchema.safeParse(pageData.metaData)
    if (!parsedMetaData.success || !parsedPageMetaData.success) {
      if (!parsedMetaData.success) {
        console.error('Error parsing metaData', parsedMetaData.error)
      }
      if (!parsedPageMetaData.success) {
        console.error('Error parsing pageMeta', parsedPageMetaData.error)
      }
      console.error('Proceeding with fallback values due to parse errors.')
    }
    const metaData = parsedMetaData.success ? parsedMetaData.data : {}
    const pageMeta = parsedPageMetaData.success ? parsedPageMetaData.data : {}

    const mainTags = parsedBody.mainStructurePath ? parsedBody.mainStructurePath.filter(tag => tag.url && (tag.url !== '/')).map(tag => tag.name) : []

    const paragraphTypesInArticle = parsedBody.paragraphs?.map(paragraph => paragraph.paragraphType) ?? []

    const embedTypesInArticle = parsedBody.paragraphs?.filter(paragraph => paragraph.paragraphType === 'embed').map(paragraph => paragraph.embed.embedType) ?? []

    const productCardsInArticle = parsedBody.paragraphs?.filter(paragraph => paragraph.paragraphType === 'productCard') ?? []
    const productCardIds = productCardsInArticle.map(paragraph => paragraph.productCard.bauerProductCard.bauerProductCardId)

    const videosInArticle = parsedBody.paragraphs?.filter(paragraph => paragraph.paragraphType === 'video') ?? []
    const videoIds = videosInArticle.map(paragraph => paragraph.video.capiId)

    const additionalTags = parsedBody.additionalStructures?.map(structure => structure.name) ?? []

    const allTags = [...mainTags, ...additionalTags]

    const teasers = parsedBody.paragraphs?.filter(paragraph => paragraph.paragraphType === 'teaserGroup').map(paragraph => paragraph.teaserGroup.teasers)
    const teaserAssetIds = teasers?.map(teaser => teaser.map(teaserItem => teaserItem.assetId).join('|'))

    const configuredLocale = process.env.APP_LOCALE as 'de-DE' | 'en-GB'
    const locale = configuredLocale?.split('-')[1]

    const composedAuthorNames = () => {
      const author = parsedBody.header?.author
      if (!author) return []

      const names = author.titleLong || author.titleShort
        ? [author.titleLong || author.titleShort]
        : []

      const additional = parsedBody.header?.additionalAuthors?.map(a =>
        a.titleLong || a.titleShort
      ) ?? []

      return [...names, ...additional]
    }

    const articleDataUsedForTracking = {
      aiGeneratedContent: Boolean(metaData.aiGeneratedContent),
      affiliateContent: Boolean(metaData.affiliateContent),
      articleId: pageMeta.nodeId ?? 'NotSet',
      articleType: pageMeta.articleType ?? 'NotSet',
      // Not used in DE due to GDPR constraints - only set authorName for GB locale
      authorName: locale === 'GB' ? composedAuthorNames().join('|') : 'NotSet',
      brand,
      categories: mainTags.join('|'),
      charCount: metaData.charCount ?? 0,
      containsAffiliate: metaData.cocoContent ?? false,
      country: locale ?? 'NotSet',
      // Please increment the dataLayerVersion by 1 when you add new fields to the dataLayer
      dataLayerVersion: 1,
      datePublished: metaData.publicationDate ?? '',
      dateCreated: metaData.dateCreated ?? '',
      dateChanged: metaData.dateChanged ?? '',
      displayDate: parsedBody.displayDate ?? '',
      embedTypes: embedTypesInArticle.join('|'),
      embedTypesCount: embedTypesInArticle.length,
      evergreenContent: Boolean(metaData.evergreenContent),
      hasAds: Boolean(!metaData.disallowAds),
      hasGallery: Boolean(metaData.hasGallery),
      hasProductCards: Boolean(parsedBody.paragraphs?.some(paragraph => paragraph.paragraphType === 'productCard') ?? false),
      hasVideo: Boolean(metaData.hasVideo),
      isContentCommerce: Boolean(metaData.isContentCommerce),
      mainCategory: parsedBody.mainStructurePath?.[1]?.name ?? '',
      mainTag: parsedBody.mainStructurePath?.at(-1)?.name ?? '',
      shorttermContent: Boolean(metaData.shorttermContent),
      pageId: pageMeta.id ?? '',
      pageviewId: uuidv4(),
      pageType: pageMeta.pageType ?? 'NotSet',
      paragraphCount: parsedBody.paragraphs?.length ?? 0,
      paragraphTypes: paragraphTypesInArticle.join('|'),
      potentialAdCount: registerSlots.value?.length ?? 0,
      productCardIds: productCardIds.join('|'),
      productCardsCount: productCardsInArticle.length ?? 0,
      roofline: parsedBody.roofline ?? '',
      subCategory: parsedBody.mainStructurePath?.[2]?.name ?? '',
      tags: allTags.join('|'),
      teaserContent: teaserAssetIds?.join('|') ?? [],
      videoIds: videoIds?.join('|') ?? [],
      wordCount: metaData.wordCount ?? 0
    } satisfies ArticleDataUsedForTracking

    // Push article meta data to Google Tag Manager Data Layer
    dataLayer.push(articleDataUsedForTracking)
  })
}
